/**
 * Microsoft Clarity Analytics Integration
 * 
 * To use:
 * 1. Sign up at https://clarity.microsoft.com
 * 2. Create a project and get your Project ID
 * 3. Add VITE_CLARITY_PROJECT_ID to your .env file
 * 4. Call initializeClarity() in your app initialization
 */

declare global {
  interface Window {
    clarity?: (action: string, ...args: any[]) => void;
  }
}

/**
 * Initialize Microsoft Clarity tracking
 * @param projectId - Your Clarity project ID (optional, defaults to env variable or hardcoded default)
 */
export function initializeClarity(projectId?: string): void {
  // Only run in production to avoid tracking local/staging sessions
  if (!import.meta.env.PROD) {
    return;
  }

  // Default project ID, can be overridden by env variable or parameter
  const defaultProjectId = 'u9put8pims';
  const clarityId = projectId || import.meta.env.VITE_CLARITY_PROJECT_ID || defaultProjectId;

  if (!clarityId) {
    if (import.meta.env.DEV) {
    }
    return;
  }

  // Check if Clarity is already initialized
  if (window.clarity) {
    return;
  }

  (function (c: Window, l: Document, a: string, r: string, i: string, t: HTMLScriptElement | null, y: Element | null) {
    (c as any)[a] = (c as any)[a] || function (...args: any[]) {
      ((c as any)[a].q = (c as any)[a].q || []).push(args);
    };
    t = l.createElement(r) as HTMLScriptElement;
    t.async = true;
    t.src = "https://www.clarity.ms/tag/" + i;
    y = l.getElementsByTagName(r)[0];
    if (y && y.parentNode) {
      y.parentNode.insertBefore(t, y);
    }
  })(window, document, "clarity", "script", clarityId, null, null);
}

/**
 * Track a custom event in Clarity
 * @param eventName - Name of the event to track
 */
export function trackClarityEvent(eventName: string): void {
  if (window.clarity) {
    window.clarity('event', eventName);
  }
}

/**
 * Identify a user in Clarity
 * @param userId - User identifier
 * @param sessionId - Optional session identifier
 */
export function identifyClarityUser(userId: string, sessionId?: string): void {
  if (window.clarity) {
    window.clarity('identify', userId, sessionId);
  }
}

